import { ScrollAxis, ScrollPane } from "../../../controls/ScrollPane";
import { ScrollPaneContent } from "../../../controls/scrollpane/ScrollPaneContent";
import { IInteractive } from "../../../interfaces/IInteractive";
import { IShowable } from "../../../interfaces/IShowable";
import { ScreenManager } from "../../../managers/ScreenManager";
import { SmartMask } from "../../../mask/SmartMask";
import { OptionsItemLayoutData, OptionsItemLiveData, OptionsItemLocData, OptionsItemThemeData } from "../../../types/types";
import { ButtonBase, ButtonEvents } from "../../../ui/ButtonBase";
import { IconButton } from "../../../ui/IconButton";
import { LabelButton } from "../../../ui/LabelButton";
import { ToggleGroup, ToggleGroupItemSelectedEventData } from "../../../ui/ToggleGroup";
import { ScreenOrientationType } from "../../../utils/GetOrientation";
import { Padding } from "../../../utils/Padding";
import { RectSize } from "../../../utils/RectSize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { OptionsDataManager } from "../../managers/OptionsDataManager";
import { AppSettings } from "../../managers/AppSettings";
import { TricksHeaderItem } from "../dealcomplete/TricksHeaderItem";
import isMobile from 'ismobilejs';


/**
 * Item List or Text paragraph
 * SetSize sets available space to put content into
 * This component doesnt care orientation (except if you need to specify content alignment-but then specify that)
 * This component has its content PIVOT at (1,1) or it's size @see SetSize (eg bottom-right)
 * Uses external objectpool @see AppObjectPool to pool items (if using item list)
 */
export class OptionsLeafPopupContentBase
    extends ScrollPane {
    /**
     * Dark rect under list items, occupies perc of screen size,
     * (based on orientation this would be perc of height or width)
     */
    public bg: Phaser.GameObjects.Image;
    public btnBack: LabelButton;

    // 'Base' paddings are paddings with OUT innerScale applied @see SetInnerScale
    protected titlePaddingBase: Padding = new Padding(0, 0, 20, 70); // left, right, top, bottom
    protected titlePadding: Padding = new Padding();
    protected borderPaddingBase: Padding = new Padding(80, 40);
    protected borderPadding: Padding = new Padding();

    protected itemLayoutData: OptionsItemLayoutData;
    protected itemThemeData: OptionsItemThemeData;
    protected itemLocData: OptionsItemLocData;
    protected itemLiveData: OptionsItemLiveData;

    // protected isShowing: boolean;
    protected innerScale: number;
    protected backTitleSpacing: number = 20;

    protected onListItemSelected: Function;
    protected onBtnBackClick: Function;

    protected btnBackHitArea: Phaser.Geom.Rectangle = new Phaser.Geom.Rectangle();


    // public Init(onBtnBackClick?: Function): void
    public override Init(
        cullItems: boolean = true,
        onBtnBackClick?: Function,
    ): void {
        // this.name = "OptionsLeafPopupContentBase";

        // ScrollPane::Init
        super.Init(cullItems);

        this.onBtnBackClick = onBtnBackClick;

        // this.toggleGroup = new ToggleGroup();
        this.size = new RectSize();
        this.titlePadding.CopyFrom(this.titlePaddingBase);
        this.borderPadding.CopyFrom(this.borderPaddingBase);

        // defaults (override in subclass or externally)
        this.scrollAxis = ScrollAxis.VERTICAL;
        this.excessScrollAmount = new Padding(0);
        // this.excessScrollAmount = new Padding(0, 0, 5, 150);
    }

    public Build(): void {
        this.visible = false;
        const _isMobile: boolean = isMobile(window.navigator).any;

        super.Build();

        // item pool for excess/unused items
        // EDIT: use AppObjectPool
        // this.itemPool = this.scene.make.container({});

        // dark background for items
        this.bg = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-black.png" });
        this.bg.setInteractive(); // consume mouse clicks (so overlay wont trigger a Hide())
        this.bg.setOrigin(1); // bottom right alignemnt of content in this component
        this.bg.alpha = 0.8;
        this.add(this.bg);

        // items container
        // this.itemsContainer = new ScrollPaneContent(this.scene, 0, this.excessScrollAmount.top);
        // this.itemsContainer.Init();
        // this.add(this.itemsContainer);
        this.content = new ScrollPaneContent(this.scene, 0, this.excessScrollAmount.top);
        this.content.Init();
        this.add(this.content);

        // mask
        this.contentMask = new SmartMask();
        // this.contentMask.DEBUG = true;
        this.contentMask.Init(this, new Phaser.Geom.Rectangle(), new Padding(0, 0));
        this.contentMask.SetContent(this.content);

        // btnBack (includes title)
        this.btnBack = new LabelButton(this.scene);
        this.btnBack.InitBase(
            _isMobile ?
                {
                    out: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-out.png" },
                    // over: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-over.png" }
                } :
                {
                    out: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-out.png" },
                    over: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-over.png" }
                },
        );
        this.btnBack.InitLabel(
            {
                out: {
                    color: AppSettings.colors.whiteString,
                    fontSize: "80px",
                    align: "left",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold80,
                },
                over: _isMobile ? undefined : {
                    // use over=out color on mobile, else you get mobile flicker frame since mobile doesnt use hower
                    color: AppSettings.colors.yellowString,
                    fontSize: "80px",
                    align: "left",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold80,
                }
            },
            new Phaser.Geom.Point(60, 0),
        );
        const btnBackScale = 1.4;
        this.btnBack.setData("baseScale", btnBackScale);
        this.btnBack.view.setScale(-btnBackScale, btnBackScale);
        this.btnBack.label.setOrigin(0, 0);
        this.btnBack.label.y = -this.btnBack.view.displayHeight * 0.5;
        this.btnBack.label.setWordWrapWidth(1050);
        this.btnBack.PostInit();
        // this.btnBack.SetRectangleHitArea(new Phaser.Geom.Rectangle(0, -8000, 16000, 8100)); // scaleX=-1, need invert rect
        this.add(this.btnBack);

        // add overlay (last)
        this.add(this.overlay);
    }

    /**
     * Overrides default Show mixin (confirmed works OK)
     * @param animated 
     */
    public override Show(animated: boolean = true): void {
        // console.log("OptionsLeafPopupContentBase::Show; animated=", animated);

        // kill tweens
        this.scene.tweens.killTweensOf(this);

        // reset 'from' (based on orientation)
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        this.x = curOrientation == ScreenOrientationType.LANDSCAPE ? this.size.width : 0;
        this.y = curOrientation == ScreenOrientationType.LANDSCAPE ? 0 : this.size.height;
        // if animate
        const genericShowDuration = OptionsDataManager.GetTiming(300);
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                x: 0,
                y: 0,
                duration: genericShowDuration,
                ease: Phaser.Math.Easing.Cubic.InOut,
                onStart: this.OnShowStart.bind(this),
                // update mask during transition
                onUpdate: this.contentMask.UpdateTransforms.bind(this.contentMask),
                onComplete: this.OnShowComplete.bind(this),
            })
        }
        else {
            this.x = 0;
            this.y = 0;
        }
        this.isShowing = true;
    }

    public override Hide(animated: boolean = true, onComplete?: Function, ignoreExternCallbacks: boolean = false): void {
        // console.log("OptionsLeafPopupContentBase::Hide; animated=", animated);

        // kill tweens
        this.scene.tweens.killTweensOf(this);

        // reset (based on orientation)
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        const toX = curOrientation == ScreenOrientationType.LANDSCAPE ? this.size.width : 0;
        const toY = curOrientation == ScreenOrientationType.LANDSCAPE ? 0 : this.size.height;

        // if animate
        let genericHideDuration = OptionsDataManager.GetTiming(300);
        // genericHideDuration = 5000;
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                x: toX,
                y: toY,
                duration: genericHideDuration,
                ease: Phaser.Math.Easing.Cubic.InOut,
                onStart: this.OnHideStart.bind(this),
                // update mask during transition
                onUpdate: this.contentMask.UpdateTransforms.bind(this.contentMask),
                onComplete: () => {
                    onComplete?.();
                    this.OnHideComplete();
                }
            })
        }
        else {
            this.x = this.size.width;
            this.y = this.size.height;
        }
        this.isShowing = false;
    }

    public IsShowing(): boolean {
        return this.isShowing;
    }

    protected OnShowStart(): void {
        // console.log("OnShowStart");
        this.visible = true;
    }

    protected OnShowComplete(): void {
        // console.log("OnShowComplete");
        this.RegisterListeners();
    }

    protected OnHideStart(): void {
        // console.log("OnHideStart");
        this.UnregisterListeners();
    }

    protected OnHideComplete(): void {
        this.visible = false;
    }

    public RegisterListeners(): void {
        super.RegisterListeners();

        this.btnBack.on(ButtonEvents.CLICK, this.OnBtnBackClick, this);

        this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.UpdateMask, this);
    }

    public UnregisterListeners(): void {
        super.UnregisterListeners();

        this.btnBack.off(ButtonEvents.CLICK, this.OnBtnBackClick);

        this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.UpdateMask);
    }

    protected OnBtnBackClick(): void {
        this.onBtnBackClick?.();
    }

    /**
     * 
     * @param onListItemSelected For contents with list items only
     */
    public SetCallback(onListItemSelected: Function = undefined): void {
        if (onListItemSelected != undefined) {
            this.onListItemSelected = onListItemSelected;
        }
    }

    protected OnListItemSelected(eventData: ToggleGroupItemSelectedEventData): void {
        // override in subclass
    }

    public SetData(
        itemLayoutData: OptionsItemLayoutData = undefined,
        itemThemeData: OptionsItemThemeData = undefined,
        itemLocData: OptionsItemLocData = undefined,
        itemLiveData: OptionsItemLiveData = undefined,
        setHelpText: boolean = false
    ): void {
        if (itemLayoutData != undefined) {
            this.itemLayoutData = itemLayoutData;
        }
        if (itemThemeData != undefined) {
            this.itemThemeData = itemThemeData;
        }
        if (itemLocData != undefined) {
            this.itemLocData = itemLocData;
        }
        if (itemLiveData != undefined) {
            this.itemLiveData = itemLiveData;
        }

        this.UpdateFromData(setHelpText);
    }

    /**
     * 
     * @param setHelpText Prefer/target/use help text node instead of any other text data node
     */
    protected UpdateFromData(setHelpText: boolean = false): void {
        // this.title.text = this.itemLocData.popupTitle;
        this.btnBack.SetText(this.itemLocData.popupTitle, false); // do not update hitArea

        // update hit area position manually
        this.btnBackHitArea.setTo(
            0,
            -8000 + this.btnBack.label.displayHeight,
            16000,
            8000
        );
        this.btnBack.SetRectangleHitArea(this.btnBackHitArea);

        // override in subclass
    }

    /**
     * 
     * @param innerScale Scale applied to padding, btnBack
     */
    public SetInnerScale(innerScale: number): void {
        this.innerScale = innerScale;
        this.titlePadding.Set(
            this.titlePaddingBase.left * innerScale,
            this.titlePaddingBase.right * innerScale,
            this.titlePaddingBase.top * innerScale,
            this.titlePaddingBase.bottom * innerScale,
        );
        this.borderPadding.Set(
            this.borderPaddingBase.left * innerScale,
            this.borderPaddingBase.right * innerScale,
            this.borderPaddingBase.top * innerScale,
            this.borderPaddingBase.bottom * innerScale,
        );

        const btnBackBaseScale: number = this.btnBack.getData("baseScale");
        // this.btnBack.setScale(-btnBackBaseScale * innerScale, btnBackBaseScale * innerScale);
        this.btnBack.setScale(btnBackBaseScale * innerScale);
        // NOTE: setting scale on TF messes up wordWrap, so we scale fontSize instead
        // this.title.setFontSize(this.title.getData("baseFontSize") * innerScale);
    }

    /**
     * Sets size for this component, in this case @see bg spans the whole size received
     * Also set inner size for overlay, mask by reducing size by padding and title size. 
     * SUBCLASS: Also, in SUBCLASS, set content size, by calculating height dep. on items therein.
     */
    public override SetSize(width: number, height: number): void {
        // console.log("SetSize", a, b);

        // size across whole bg (includes title, button, content)
        this.size.Set(width, height);

        this.bg.setDisplaySize(width, height);

        const titlePosY = - height + this.borderPadding.top + this.titlePadding.top;
        const contentPosX = - width + this.borderPadding.left;
        // const contentPosY = titlePosY + this.title.displayHeight + this.titlePadding.bottom;
        const contentPosY = titlePosY + this.btnBack.label.displayHeight * this.btnBack.scaleY + this.titlePadding.bottom;
        const btnBackWidthHalf = Math.abs((this.btnBack.view.displayWidth / 2) * this.btnBack.scaleX);
        const btnBackHeightHalf = Math.abs((this.btnBack.view.displayHeight / 2) * this.btnBack.scaleY);

        this.btnBack.setPosition(
            contentPosX + btnBackWidthHalf - 7, // shadow size
            titlePosY + btnBackHeightHalf + 2,
        );

        // title position changed (multiline?)
        // > update position of content, contentMask, cachedOverlayHitArea, overlay
        this.cachedOverlayHitArea.setPosition(contentPosX, contentPosY);
        // this.overlay.setPosition(contentPosX, contentPosY);
        this.content.setPosition(contentPosX, contentPosY);
        this.contentMask.SetPosition(contentPosX, contentPosY);
        // console.log("SetSize: contentMask.SetPosition:", contentPosX+"("+this.contentMask.x+")", contentPosY);

        // following needs to be done in SUBCLASS (or mask wont draw)
        const overlayWidth = width - this.borderPadding.left - this.borderPadding.right;
        // const overlayHeight = height - this.borderPadding.top - this.titlePadding.top - this.title.displayHeight - this.titlePadding.bottom - this.borderPadding.bottom;
        const overlayHeight = height - this.borderPadding.top - this.titlePadding.top - this.btnBack.label.displayHeight * this.btnBack.scaleY - this.titlePadding.bottom - this.borderPadding.bottom;

        // sizes: cachedOverlayHitArea, overlay, content width (content height in SUBCLASS)
        this.cachedOverlayHitArea.setSize(overlayWidth, overlayHeight);
        // this.overlay.setDisplaySize(overlayWidth, overlayHeight);
        // this.content.GetSize().width = width;
        this.content.SetSize(overlayWidth, 0);

        // this.contentMask.SetMaskRectSize(overlayWidth, overlayHeight);
        // // update transforms as well, since this container's position might have changed also
        // this.contentMask.UpdateTransforms();

        // // > lastScrollDelta 0 an update scroll
        // this.UpdateInPlace();

        // override in subclass
    }

    /**
     * BugFix: One OnResize not enough to update mask (is this due to parent transform matrix not updating in the same frame?)
     * > keep updating as long as visible.
     */
    private UpdateMask(): void {
        this.contentMask.UpdateTransforms();
        this.contentMask.UpdateGraphics();
    }
}